import React, { useEffect, useState, useRef } from 'react';
import moment from 'moment';
import { ICommuteItemData } from 'types/commuting';
import './CommuteModify.scss';
import TimeInput from 'common/time-input/time-input';
import { formatKoreanTimeToMinuteValue, onlyNumberInput, removeKoreanWordTime } from 'constants/utils';
import RadioButton from 'common/radio/radio';
import styleGuide from 'constants/styleGuide';
import useAlertConfirmStore from '../../../zustands/useAlertConfirmStore';

export interface IStateModal {
  MEMBER_SEQ: number;
  EMP_ID: number;
  NAME: string;
  DATE: string;
  START: string;
  END: string;
  START_M: Date | string | null;
  END_M: Date | string | null;
  QRSTART: string;
  QREND: string;
  QRSTART_M: Date | string | null;
  QREND_M: Date | string | null;
  RESTTIME: number;
  TYPE: string;
  STATUS: string;
}

interface IProps {
  modalState: ICommuteItemData | null;
  onClose: () => void;
  restTimeInput: React.RefObject<HTMLInputElement>;
  modifyCommuteItem: (state: IStateModal, sch_id: number) => void;
  addSchDelete: (SCH_ID: number) => void;
  getAnnualData: (EMP_SEQ: number, date: string) => void;
  backModifyModal: () => void;
  setHoliday: (type: '유급' | '무급', weekAllowanceType?: string) => void;
  cancelHoliday: () => void;
}

function CommuteModify({
  modalState,
  onClose,
  restTimeInput,
  modifyCommuteItem,
  addSchDelete,
  getAnnualData,
  backModifyModal,
  setHoliday,
  cancelHoliday,
}: IProps) {
  const { setModalState: setAlertModalState } = useAlertConfirmStore();

  const [state, setState] = useState<IStateModal>({
    MEMBER_SEQ: 0,
    EMP_ID: 0,
    NAME: '',
    DATE: '',
    START: '',
    END: '',
    START_M: null,
    END_M: null,
    QRSTART: '',
    QREND: '',
    QRSTART_M: null,
    QREND_M: null,
    RESTTIME: 0,
    TYPE: '',
    STATUS: '0',
  });
  const [holidayType, setHolidayType] = useState<'유급' | '무급'>('유급');
  const [weekendAllowanceType, setWeekendAllowanceType] = useState<string>('2');

  const timeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;
    if (name === 'RESTTIME') {
      setState({ ...state, RESTTIME: Number(onlyNumberInput(value)) });
    } else {
      const hour = Number(value.split(':')[0]);
      const min = Number(value.split(':')[1]);
      const time = moment(moment().hour(hour).minute(min)).toDate();
      setState({ ...state, [name]: value });
    }
  };
  // 출퇴근기록 수정
  function clickModify() {
    modifyCommuteItem(state, modalState?.SCH_ID ?? 0);
  }

  function CloseCommuteModModal() {
    onClose();
  }

  const handleStatus = (name: string, value: string) => {
    setState((prev) => ({ ...prev, STATUS: value }));
  };

  // 정상출퇴근 시간 설정
  const setScheduleTime = (type: 'start' | 'end') => {
    if (type === 'start') {
      if (modalState?.NEW_CHANGE_START && typeof modalState.NEW_CHANGE_START === 'string') {
        setState((prev) => ({ ...prev, QRSTART_M: removeKoreanWordTime(modalState.NEW_CHANGE_START ?? '') }));
      } else {
        setState((prev) => ({ ...prev, QRSTART_M: removeKoreanWordTime(state.START) }));
      }
    } else {
      if (modalState?.NEW_CHANGE_END && typeof modalState.NEW_CHANGE_END === 'string') {
        setState((prev) => ({ ...prev, QREND_M: removeKoreanWordTime(modalState.NEW_CHANGE_END ?? '') }));
      } else {
        setState((prev) => ({ ...prev, QREND_M: removeKoreanWordTime(state.END) }));
      }
    }
  };

  // 휴무시 타입 변경
  const handleChangeHolidayType = (name: string, value: string) => {
    if (name === 'holidayTypeSelect' && (value === '유급' || value === '무급')) {
      setHolidayType(value);
    } else if (name === 'weekendAllowanceTypeSelect') {
      setWeekendAllowanceType(value);
    }
  };

  function renderAllowance({ STATUS }: { STATUS: string }) {
    return (
      <div className="AllowanceBox">
        <div className="AllowanceItem">
          <RadioButton
            text="적용(1배)"
            radioName="Allowance"
            value="9"
            isChecked={STATUS === '9'}
            checked={handleStatus}
          />
        </div>
        <div className="AllowanceItem">
          <RadioButton
            text="직원별 급여정보 설정대로 적용"
            radioName="Allowance"
            value="0"
            isChecked={STATUS === '0'}
            checked={handleStatus}
          />
        </div>
      </div>
    );
  }
  const RenderAllowance = React.memo(renderAllowance);

  useEffect(() => {
    let restTimeUnit = 0;
    if (modalState?.REST_TIME) {
      if (modalState?.REST_TIME.toString().split(onlyNumberInput(modalState?.REST_TIME.toString()))[1] === '시간') {
        restTimeUnit = Number(onlyNumberInput(modalState?.REST_TIME.toString()));
      } else {
        restTimeUnit = Number(onlyNumberInput(modalState?.REST_TIME.toString()));
      }
    }

    setState({
      MEMBER_SEQ: modalState?.MEMBER_SEQ ?? 0,
      EMP_ID: modalState?.EMP_ID ?? 0,
      NAME: modalState?.NAME ?? '',
      DATE: typeof modalState?.DATE === 'string' ? modalState?.DATE : '',
      START: typeof modalState?.NEW_START === 'string' ? modalState?.NEW_START : '',
      END: typeof modalState?.NEW_END === 'string' ? modalState?.NEW_END : '', // 일정퇴근
      START_M:
        typeof modalState?.NEW_CHANGE_START === 'string' ? removeKoreanWordTime(modalState?.NEW_CHANGE_START) : null, // 일정수정출근
      END_M: typeof modalState?.NEW_CHANGE_END === 'string' ? removeKoreanWordTime(modalState?.NEW_CHANGE_END) : null, // 일정수정퇴근
      QRSTART: typeof modalState?.NEW_START_TIME === 'string' ? modalState?.NEW_START_TIME : '', // qr출근
      QREND: typeof modalState?.NEW_END_TIME === 'string' ? modalState?.NEW_END_TIME : '', // qr퇴근
      QRSTART_M:
        typeof modalState?.NEW_UPDATED_START === 'string' ? removeKoreanWordTime(modalState?.NEW_UPDATED_START) : null, // qr수정출근
      QREND_M:
        typeof modalState?.NEW_UPDATED_END === 'string' ? removeKoreanWordTime(modalState?.NEW_UPDATED_END) : null, // qr수정퇴근
      RESTTIME: modalState?.REST_TIME ? formatKoreanTimeToMinuteValue(modalState?.REST_TIME) : 0, // 휴게시간
      TYPE: modalState?.SCH_TYPE == '추가일정' ? '1' : '0',
      STATUS: modalState?.STATUS ?? '',
    });
  }, [modalState]);

  return (
    <div className="modModal">
      <div className="modModalCnt">
        {modalState?.viewType === 'modify' ? (
          <>
            {/* 상단 타이틀과 설명글, 그리고 날짜 들어 있음 */}
            <div className="modModalCntTop">
              <div className="topLeft">
                <strong>{state.DATE}</strong>
                <span>{modalState?.NAME} 직원의 일정 및 출퇴근시간을 수정할 수 있습니다.</span>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span
                    style={{
                      fontSize: 10,
                    }}>{`* 출퇴근 기록 근무일정을 수정하여도 기존 근무일정대로 주휴수당이 계산됩니다.`}</span>
                  <span
                    style={{
                      fontSize: 10,
                      marginLeft: 7,
                    }}>
                    {`만약 수정한 근무일정대로 주휴수당을 변경하려면 직원목록 > 정보수정 > 근무일정에서 근무일정을 수정해주세요.`}
                  </span>
                </div>
              </div>
            </div>
            <div className="modModalCntBody">
              {modalState?.SCH_TYPE !== '자율출퇴근' && (
                <>
                  <div className="modModalCntSection">
                    <div className="sectionLeft">일정출근</div>
                    <div className="sectionRight">
                      <div>{state.START}</div>
                    </div>
                  </div>
                  <div className="modModalCntSection">
                    <div className="sectionLeft">일정퇴근</div>
                    <div className="sectionRight">
                      <div>{state.END}</div>
                    </div>
                  </div>
                  <div className="modModalCntSection">
                    <div className="sectionLeft">수정 - 일정출근</div>
                    <div className="sectionRight">
                      <TimeInput
                        inputName="START_M"
                        value={typeof state.START_M === 'string' ? state.START_M : undefined}
                        onChange={timeChange}
                        iconSide="RIGHT"
                      />
                    </div>
                  </div>
                  <div className="modModalCntSection">
                    <div className="sectionLeft">수정 - 일정퇴근</div>
                    <div className="sectionRight">
                      <TimeInput
                        inputName="END_M"
                        value={typeof state.END_M === 'string' ? state.END_M : undefined}
                        onChange={timeChange}
                        iconSide="RIGHT"
                      />
                    </div>
                  </div>
                </>
              )}
              <div className="modModalCntSection">
                <div className="sectionLeft">실제 출근시간</div>
                <div className="sectionRight">
                  <div>{state.QRSTART}</div>
                </div>
              </div>
              <div className="modModalCntSection">
                <div className="sectionLeft">실제 퇴근시간</div>
                <div className="sectionRight">
                  <div>{state.QREND}</div>
                </div>
              </div>
              <div className="modModalCntSection">
                <div className="sectionLeft">수정 - 실제 출근시간</div>
                <div className="sectionRight">
                  {modalState?.SCH_TYPE !== '자율출퇴근' && (
                    <button onClick={() => setScheduleTime('start')}>정상출근</button>
                  )}
                  <TimeInput
                    inputName="QRSTART_M"
                    value={typeof state.QRSTART_M === 'string' ? state.QRSTART_M : undefined}
                    onChange={timeChange}
                    iconSide="RIGHT"
                  />
                </div>
              </div>
              <div className="modModalCntSection">
                <div className="sectionLeft">수정 - 실제 퇴근시간</div>
                <div className="sectionRight">
                  {modalState?.SCH_TYPE !== '자율출퇴근' && (
                    <button onClick={() => setScheduleTime('end')}>정상퇴근</button>
                  )}
                  <TimeInput
                    inputName="QREND_M"
                    value={typeof state.QREND_M === 'string' ? state.QREND_M : undefined}
                    onChange={timeChange}
                    iconSide="RIGHT"
                  />
                </div>
              </div>
              <div className="modModalCntSection">
                <div className="sectionLeft">휴게시간</div>
                <div className="sectionRight">
                  <div className="restTimeInputArea">
                    <div className="restTimeText">
                      <span>분</span>
                    </div>
                    <input
                      ref={restTimeInput}
                      className="restTimeInput"
                      type="text"
                      name="RESTTIME"
                      value={state.RESTTIME}
                      onChange={timeChange}
                      maxLength={3}
                    />
                  </div>
                </div>
              </div>
              {modalState?.PAYTYPE == '0' && (
                <div className="modModalCntSection">
                  <div className="sectionLeft">수당포함여부</div>
                  <div className="sectionRight">
                    <RenderAllowance STATUS={state.STATUS} />
                  </div>
                </div>
              )}
              <div className="modModalBtnArea">
                <button onClick={clickModify}>수정</button>
                <button onClick={CloseCommuteModModal}>취소</button>
                {modalState?.SCH_STATUS == '휴무' ? (
                  <button className="holiday3" onClick={cancelHoliday}>
                    <span>휴무취소</span>
                  </button>
                ) : (
                  <button
                    className="holiday"
                    onClick={() =>
                      getAnnualData(
                        modalState?.EMP_ID ?? 0,
                        typeof modalState?.DATE === 'string' ? modalState?.DATE : '',
                      )
                    }>
                    <span>휴무적용</span>
                  </button>
                )}
                {state.TYPE == '1' ? (
                  <button
                    onClick={() => {
                      if (modalState.SCH_ID) {
                        setAlertModalState({
                          isModalShow: true,
                          title: '추가일정 삭제',
                          desc: `추가일정 삭제 시 기록된 추가일정의 출퇴근기록도 함께 삭제됩니다. \n\n정말 삭제하시겠습니까?`,
                          okBtnText: '확인',
                          closeBtnText: '취소',
                          okCallback: () => {
                            if (modalState.SCH_ID) {
                              addSchDelete(modalState.SCH_ID);
                            }
                          },
                        });
                      }
                    }}>
                    추가일정 삭제
                  </button>
                ) : null}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* 상단 타이틀과 설명글, 그리고 날짜 들어 있음 */}
            <div className="modModalCntTop">
              <div className="topLeft">
                <strong>{state.DATE}</strong>
                <span>{modalState?.NAME} 직원을 휴무로 설정합니다.</span>
              </div>
              <div className="topRight">
                <div>
                  <RadioButton
                    checked={handleChangeHolidayType}
                    text="유급휴무"
                    radioName="holidayTypeSelect"
                    value={'유급'}
                    isChecked={holidayType === '유급'}
                  />
                </div>
                <div>
                  <RadioButton
                    checked={handleChangeHolidayType}
                    text="무급휴무"
                    radioName="holidayTypeSelect"
                    value={'무급'}
                    isChecked={holidayType === '무급'}
                  />
                </div>
              </div>
            </div>
            <div className="modModalCntBody">
              {holidayType === '유급' ? (
                <>
                  <div className="modModalCntSection2">
                    <div className="sectionLeft">총 연차</div>
                    <div className="sectionRight">
                      <div>{Number(modalState?.ANNUAL)}</div>
                    </div>
                  </div>
                  <div className="modModalCntSection">
                    <div className="sectionLeft">사용한 연차</div>
                    <div className="sectionRight">
                      <div>{Number(modalState?.USE_ANNUAL)}</div>
                    </div>
                  </div>
                  <div className="modModalCntSection2">
                    <div className="sectionLeft">남은 연차</div>
                    <div className="sectionRight">
                      <div>{Number(modalState?.ANNUAL) - Number(modalState?.USE_ANNUAL)}</div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="modModalCntSection3">
                  <RadioButton
                    checked={handleChangeHolidayType}
                    text="주휴수당 당일 포함하여 적용"
                    radioName="weekendAllowanceTypeSelect"
                    value={'2'}
                    isChecked={weekendAllowanceType === '2'}
                    containerStyle={{ padding: '10px 0' }}
                    circleStyle={{ accentColor: styleGuide.palette.primary }}
                  />
                  <RadioButton
                    checked={handleChangeHolidayType}
                    text="주휴수당 당일 미포함하여 적용"
                    radioName="weekendAllowanceTypeSelect"
                    value={'1'}
                    isChecked={weekendAllowanceType === '1'}
                    containerStyle={{ padding: '10px 0' }}
                    circleStyle={{ accentColor: styleGuide.palette.primary }}
                  />
                  <RadioButton
                    checked={handleChangeHolidayType}
                    text="주휴수당 이번주 미적용"
                    radioName="weekendAllowanceTypeSelect"
                    value={'0'}
                    isChecked={weekendAllowanceType === '0'}
                    containerStyle={{ padding: '10px 0' }}
                    circleStyle={{ accentColor: styleGuide.palette.primary }}
                  />
                </div>
              )}
              <div className="modModalBtnArea">
                {holidayType === '유급' ? (
                  <>
                    {Number(modalState?.ANNUAL) - Number(modalState?.USE_ANNUAL) > 0 ? (
                      <button className="holiday" onClick={() => setHoliday('유급')}>
                        유급휴무 적용
                      </button>
                    ) : (
                      <button className="holiday2" onClick={() => setHoliday('유급')}>
                        유급휴무 적용
                      </button>
                    )}
                  </>
                ) : (
                  <button className="holiday" onClick={() => setHoliday('무급', weekendAllowanceType)}>
                    무급휴무 적용
                  </button>
                )}
                <button onClick={backModifyModal}>이전</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default React.memo(CommuteModify);
